import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import type { Consultant } from '../../types/cms';
import type { AdminCommon } from '../../pages/Admin';
import { resolveConsultantImage } from '../../lib/images';

const IMAGE_LIMIT_KB = 500;

export default function ConsultantsManager({ draft, onSave, notify }: AdminCommon) {
  const [list, setList] = useState<Consultant[]>(draft.consultants || []);
  const [editing, setEditing] = useState<Consultant | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setList(draft.consultants || []);
  }, [draft]);

  const startAdd = () => {
    const nextId = Math.max(0, ...list.map((c) => c.id)) + 1;
    setEditing({
      id: nextId,
      nameEn: '',
      nameTc: '',
      nameSc: '',
      titleEn: '',
      titleTc: '',
      titleSc: '',
      descriptionEn: '',
      descriptionTc: '',
      descriptionSc: '',
      imageUri: '',
      sortOrder: list.length + 1,
      active: true,
    });
  };

  const patch = (fields: Partial<Consultant>) => setEditing((prev) => (prev ? { ...prev, ...fields } : prev));

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    if (file.size > IMAGE_LIMIT_KB * 1024) {
      notify(`圖片不能超過 ${IMAGE_LIMIT_KB}KB，請先壓縮。`, 'error');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => patch({ imageUri: reader.result as string });
    reader.readAsDataURL(file);
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...list];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setList(next.map((c, i) => ({ ...c, sortOrder: i + 1 })));
    if (editing) {
      const reIdx = next.findIndex((c) => c.id === editing.id);
      if (reIdx >= 0) patch({ sortOrder: reIdx + 1 });
    }
  };

  const remove = (id: number) => {
    if (!window.confirm('確定刪除此顧問？儲存後生效（尚未發布前可「重設草稿」還原）。')) return;
    const next = list.filter((c) => c.id !== id).map((c, i) => ({ ...c, sortOrder: i + 1 }));
    setList(next);
    if (editing?.id === id) setEditing(null);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const finalList = list.map((c) => (c.id === editing?.id ? editing : c));
      // Normalize sort order by array position.
      const normalized = finalList
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((c, i) => ({ ...c, sortOrder: i + 1 }));
      await onSave({ consultants: normalized });
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const field = (
    label: string,
    key: 'nameEn' | 'nameTc' | 'nameSc' | 'titleEn' | 'titleTc' | 'titleSc',
    required = false
  ) => (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input
        className="w-full border border-gray-200 rounded p-2 text-sm focus:ring-2 focus:ring-navy-100 outline-none"
        value={editing?.[key] || ''}
        required={required}
        onChange={(e) => patch({ [key]: e.target.value } as Partial<Consultant>)}
      />
    </div>
  );

  const area = (label: string, key: 'descriptionEn' | 'descriptionTc' | 'descriptionSc') => (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <textarea
        rows={5}
        className="w-full border border-gray-200 rounded p-2 text-sm focus:ring-2 focus:ring-navy-100 outline-none"
        value={editing?.[key] || ''}
        onChange={(e) => patch({ [key]: e.target.value } as Partial<Consultant>)}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Editor */}
      {editing && (
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-navy-900">
              {list.some((c) => c.id === editing.id) ? `編輯顧問 #${editing.id}` : '新增顧問'}
            </h2>
            <button onClick={() => setEditing(null)} className="text-sm text-gray-500 hover:text-gray-800">
              取消
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {field('姓名 (EN)', 'nameEn', true)}
            {field('姓名 (繁體)', 'nameTc', true)}
            {field('姓名 (简体)', 'nameSc', true)}
            {field('職銜 (EN)', 'titleEn', true)}
            {field('職銜 (繁體)', 'titleTc', true)}
            {field('職銜 (简体)', 'titleSc', true)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {area('簡介 (EN)', 'descriptionEn')}
            {area('簡介 (繁體)', 'descriptionTc')}
            {area('簡介 (简体)', 'descriptionSc')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="border p-4 rounded bg-gray-50">
              <label className="block text-xs text-gray-500 mb-2">
                大頭照（檔名對應內建圖片、http(s) URL 或上傳 ≤{IMAGE_LIMIT_KB}KB）
              </label>
              <div className="flex gap-2 items-center">
                <img
                  src={resolveConsultantImage(editing.imageUri)}
                  alt="preview"
                  className="h-16 w-16 rounded object-cover border bg-white"
                />
                <div className="flex-1">
                  <input
                    className="w-full border border-gray-200 rounded p-2 text-xs mb-2 bg-white"
                    placeholder="Eric_Tsang.jpg 或 https://…"
                    value={editing.imageUri?.startsWith('data:') ? '（已上傳圖片）' : editing.imageUri || ''}
                    readOnly={editing.imageUri?.startsWith('data:')}
                    onChange={(e) => patch({ imageUri: e.target.value })}
                  />
                  <label className="inline-flex items-center gap-1 text-xs bg-navy-900 text-white px-3 py-1.5 rounded cursor-pointer hover:bg-navy-800">
                    <Upload className="h-3.5 w-3.5" /> 上傳圖片
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                  </label>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">顯示順序</label>
              <input
                type="number"
                className="w-full border border-gray-200 rounded p-2 text-sm"
                value={editing.sortOrder}
                onChange={(e) => patch({ sortOrder: Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => patch({ active: e.target.checked })}
                />
                在網站顯示此顧問
              </label>
            </div>
          </div>

          <button
            onClick={() => {
              if (list.some((c) => c.id === editing.id)) {
                setList(list.map((c) => (c.id === editing.id ? editing : c)));
              } else {
                setList([...list, editing]);
              }
              notify('已加入清單，記得點擊下方「儲存全部到草稿」。');
              setEditing(null);
            }}
            className="mt-4 bg-navy-900 text-white px-6 py-2 rounded hover:bg-navy-800 transition-colors text-sm"
          >
            確認加入清單
          </button>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-navy-900">顧問名單（{list.length} 位）</h2>
          <div className="flex gap-2">
            <button
              onClick={startAdd}
              className="flex items-center gap-1 text-sm px-4 py-2 rounded border border-navy-200 text-navy-700 hover:bg-navy-50"
            >
              <Plus className="h-4 w-4" /> 新增
            </button>
            <button
              onClick={saveAll}
              disabled={saving}
              className="flex items-center gap-1 text-sm px-4 py-2 rounded bg-gold-500 text-navy-900 font-bold hover:bg-gold-600 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {saving ? '儲存中…' : '儲存全部到草稿'}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {list.map((c, i) => (
            <div
              key={c.id}
              className={`flex items-center gap-3 border rounded-lg p-3 ${
                !c.active ? 'opacity-50 bg-gray-50' : ''
              }`}
            >
              <img
                src={resolveConsultantImage(c.imageUri)}
                alt={c.nameEn}
                className="h-12 w-12 rounded-full object-cover border"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {c.nameTc || c.nameEn} <span className="text-gray-400 font-normal">/ {c.nameEn}</span>
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {c.titleTc || c.titleEn} · 順序 {c.sortOrder} {c.active ? '' : '· 已隱藏'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} className="p-1.5 text-gray-400 hover:text-navy-700" title="上移">
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button onClick={() => move(i, 1)} className="p-1.5 text-gray-400 hover:text-navy-700" title="下移">
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button onClick={() => setEditing({ ...c })} className="text-sm text-blue-600 hover:text-blue-800 px-2">
                  編輯
                </button>
                <button onClick={() => remove(c.id)} className="p-1.5 text-red-400 hover:text-red-600" title="刪除">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-center text-gray-400 py-8">沒有顧問，點「新增」開始。</p>}
        </div>
      </div>
    </div>
  );
}
